<div class="booking-account-shell">
  <aside class="account-sidebar" [class.is-open]="sidebarOpen" aria-label="Account navigation">
    <div class="sidebar-logo">
      <a routerLink="/" [attr.aria-label]="operatorName + ' home'">
        <img [src]="logo" [alt]="operatorName">
      </a>
    </div>

    <div class="account-summary">
      <span class="account-avatar" aria-hidden="true">{{ userInitials }}</span>
      <div>
        <strong>{{ userName }}</strong>
        <span>{{ memberSince }}</span>
      </div>
    </div>

    <nav class="account-navigation">
      <a routerLink="/my-booking" (click)="closeSidebar()">
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <rect x="3" y="3" width="7" height="9" rx="1"/>
          <rect x="14" y="3" width="7" height="5" rx="1"/>
          <rect x="14" y="12" width="7" height="9" rx="1"/>
          <rect x="3" y="16" width="7" height="5" rx="1"/>
        </svg>
        My bookings
      </a>
      <a routerLink="/user-profile" class="is-active" (click)="closeSidebar()">
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <circle cx="12" cy="8" r="4"/>
          <path d="M4 21c0-4 3.6-7 8-7s8 3 8 7"/>
        </svg>
        My profile
      </a>
      <button type="button" (click)="bookBus()">
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <circle cx="11" cy="11" r="7"/>
          <path d="m20 20-3.5-3.5"/>
        </svg>
        Book a bus
      </button>
      <span class="nav-divider"></span>
      <button type="button" (click)="logoutUser()">
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
          <path d="m16 17 5-5-5-5M21 12H9"/>
        </svg>
        Log out
      </button>
    </nav>
  </aside>

  @if (sidebarOpen) {
    <button
      type="button"
      class="sidebar-overlay"
      aria-label="Close account navigation"
      (click)="closeSidebar()">
    </button>
  }

  <main class="booking-account-main">
    <header class="account-topbar">
      <button
        type="button"
        class="sidebar-toggle"
        aria-label="Open account navigation"
        (click)="openSidebar()">
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M3 6h18M3 12h18M3 18h18"/>
        </svg>
      </button>
      <h1>My profile</h1>
    </header>

    <div class="profile-content">
      @if (!isLoggedIn) {
        <section class="booking-empty-state">
          <span class="empty-icon">
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <circle cx="12" cy="8" r="4"/>
              <path d="M4 21c0-4 3.6-7 8-7s8 3 8 7"/>
            </svg>
          </span>
          <h2>Sign in to see your profile</h2>
          <p>Use your one-time code to access your account information.</p>
          <button
            type="button"
            class="primary-action"
            data-toggle="modal"
            data-target="#loginPopupForm">
            Sign in
          </button>
        </section>
      } @else if (profileLoading) {
        <section class="profile-card profile-loading" aria-busy="true">
          <span class="profile-title-skeleton"></span>
          <div class="profile-form-grid">
            @for (placeholder of [1, 2, 3, 4]; track placeholder) {
              <div>
                <span class="profile-label-skeleton"></span>
                <span class="profile-input-skeleton"></span>
              </div>
            }
          </div>
        </section>
      } @else {
        @if (errorMessage) {
          <div class="profile-notice is-error" role="alert">{{ errorMessage }}</div>
        }
        @if (successMessage) {
          <div class="profile-notice is-success" role="status">{{ successMessage }}</div>
        }

        <section class="profile-card">
          <div class="profile-card-heading">
            <div>
              <h2>Personal information</h2>
              <p>Keep your account and booking contact details up to date.</p>
            </div>
            <span class="otp-account-badge">OTP secured</span>
          </div>

          <form [formGroup]="profileForm" (ngSubmit)="saveProfile()" novalidate>
          <div class="profile-form-grid">
            <label>
              <span>First name</span>
              <input type="text" formControlName="first_name" autocomplete="given-name" [readonly]="!isEditing || profileSaving">
              @if (isEditing && profileForm.get('first_name')?.touched && profileForm.get('first_name')?.invalid) {
                <small>Please enter at least 2 characters.</small>
              }
            </label>
            <label>
              <span>Last name</span>
              <input type="text" formControlName="last_name" autocomplete="family-name" [readonly]="!isEditing || profileSaving">
              @if (isEditing && profileForm.get('last_name')?.touched && profileForm.get('last_name')?.invalid) {
                <small>Please enter at least 2 characters.</small>
              }
            </label>
            <label>
              <span>Email address</span>
              <input type="email" formControlName="email" autocomplete="email" placeholder="Not provided" [readonly]="!isEditing || profileSaving">
              @if (isEditing && profileForm.get('email')?.touched && profileForm.get('email')?.invalid) {
                <small>Enter a valid email address.</small>
              }
            </label>
            <label>
              <span>Phone number</span>
              <input type="tel" formControlName="phone_number" autocomplete="tel" placeholder="e.g. +254712345678" [readonly]="!isEditing || profileSaving">
              @if (isEditing && profileForm.get('phone_number')?.touched && profileForm.get('phone_number')?.invalid) {
                <small>Enter a valid international phone number.</small>
              }
            </label>
          </div>

          <div class="profile-actions">
            @if (isEditing) {
              <button type="button" class="profile-cancel-button" [disabled]="profileSaving" (click)="cancelEditing()">Cancel</button>
              <button type="submit" class="profile-support-button" [disabled]="profileSaving">
                {{ profileSaving ? 'Saving…' : 'Save changes' }}
              </button>
              <span>Use your international phone format, including the country code.</span>
            } @else {
              <button type="button" class="profile-support-button" (click)="editProfile()">Edit profile</button>
              <span>Your updated details will be used for future bookings.</span>
            }
          </div>
          </form>
        </section>

        <section class="profile-card profile-security-card">
          <span class="security-icon">
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
              <path d="m9 12 2 2 4-4"/>
            </svg>
          </span>
          <div>
            <h2>Password-free sign in</h2>
            <p>Your account uses a one-time code sent to your verified mobile number or email. There is no password to remember or change.</p>
          </div>
        </section>
      }
    </div>
  </main>
</div>
